// File Location: src/screens/financial/FinancialReviewForm.js

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Info, 
  Upload, 
  X, 
  CheckCircle, 
  AlertTriangle,
  Save,
  Loader,
  User,
  Home,
  Phone,
  Mail,
  Building,
  CreditCard,
  DollarSign,
  FileText,
  XCircle
} from 'lucide-react';

const FinancialReviewForm = ({
  application,
  onSubmit,
  onClose,
  autoSaveData,
  onAutoSave,
  onRequestInfo,
  hasPendingClarification = false,
  clarificationDetails = null
}) => {
  const { userData } = useAuth();
  const user = { ...userData, uid: userData?.uid } || {};

  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [reviewStartTime] = useState(new Date());

  // Check if the application is already approved/completed
  const isFinancialApprovedBase = (
    application?.workflow?.financialVerification?.status === 'approved' ||
    application?.status === 'financial_approved'
  );
    // Override: enable editing if KYP is challenged and reassigned to Finance
    // Only while reassignment is pending for Finance; lock again when completed
    const kypReassign = application?.workflow?.medicalVerification?.kypReassignment || null;
    const reassignedToFinance = (
      kypReassign && (
        kypReassign.target === 'finance' || (kypReassign.targets || [])?.includes?.('finance')
      )
    );
    const reassignStatus = kypReassign?.status || 'pending';
    const isReassignmentActiveForFinance = reassignedToFinance && !['finance_completed', 'completed'].includes(reassignStatus);
    const isChallengedForFinance = (
      application?.workflow?.medicalVerification?.kypHospitalAcknowledgement?.status === 'challenged' &&
      isReassignmentActiveForFinance
    );

    const isReadOnly = isFinancialApprovedBase && !isChallengedForFinance;

  // Auto-save timer
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  
  // Track if form has been initialized from application data
  const isInitializedRef = useRef(false);

  // Validation and UI state
  const [validationErrors, setValidationErrors] = useState({});

  // Form state
  const [financialData, setFinancialData] = useState({
    status: "",
    comments: "",
  // New: Payment preference selection
  // Values: 'hospital_pdc' | 'loan' (empty means not selected yet)
  paymentOption: '',
    personalDetails: {
      isPatientBorrowerSame: false,
      patientName: "",
      borrowerName: "",
      relationToBorrower: "",
      borrowerDateOfBirth: "",
      contactNumber: "",
      emailId: "",
      occupation: "",
      companyName: "",
      aadharNumber: "",
      panNumber: "",
      alternateContactNumber: "",
      patientGender: "Male",
      borrowerGender: "Male",
      borrowerMaritalStatus: ""
    },
    addressDetails: {
      isCurrentPermanentSame: false,
      permanentAddress: {
        pincode: "",
        state: "",
        district: "",
        city: "",
        address: ""
      },
      currentAddress: {
        pincode: "",
        state: "",
        district: "",
        city: "",
        address: ""
      }
    },
    referenceDetails: {
      name: "",
      contactNumber: "",
      relationship: "",
      emailId: ""
    },
    hospitalInfo: {
      name: "",
      treatmentDetails: ""
    },
    borrowerBankDetails: {
      bankName: "",
      branch: "",
      accountNumber: "",
      ifscCode: ""
    },
    loanDetails: {
      totalMonthlyIncome: "",
      loanAmount: "",
      schemeMonths: "12",
      interestSubventionPercent: "",
  processingFeePercent: "",
  lenderName: "",
  loanId: ""
    },
    documents: {
      aadharCardFront: { file: null, url: "", uploaded: false },
      aadharCardBack: { file: null, url: "", uploaded: false },
      borrowerPanCard: { file: null, url: "", uploaded: false },
      relationshipProof: { file: null, url: "", uploaded: false },
      cancelledCheque: { file: null, url: "", uploaded: false },
      incomeProof: { file: null, url: "", uploaded: false },
      hospitalBill: { file: null, url: "", uploaded: false },
      bankStatement: { file: null, url: "", uploaded: false }
    },
    eligibilityConfirmed: false,
    approvedLoanAmount: "",
    finalDecision: ""
  });

  // Preview modal state for submitted documents (from hospital application)
  const [preview, setPreview] = useState({ open: false, url: '', name: '', type: '' });
  const openPreview = (doc) => {
    if (!doc) return;
    const url = doc.url || '';
    const name = doc.fileName || 'Document';
    const lower = (name || url).toLowerCase();
    const type = lower.endsWith('.pdf') || url.includes('content-type=application%2Fpdf') ? 'pdf' :
      (/(png|jpe?g|gif|webp|bmp)$/i.test(lower) ? 'image' : 'other');
    setPreview({ open: true, url, name, type });
  };
  const closePreview = () => setPreview({ open: false, url: '', name: '', type: '' });

  // Initialize form data from autoSaveData if available
  useEffect(() => {
    if (autoSaveData) {
      setFinancialData(prevData => ({
        ...prevData,
        ...autoSaveData
      }));
    }
  }, [autoSaveData]);

  // Auto-populate from application data when component mounts
  useEffect(() => {
    if (application && !isInitializedRef.current) {
      // Check if there's already saved data (draft) - if so, don't overwrite with application data
      const hasExistingData = autoSaveData?.personalDetails?.patientName || 
                              autoSaveData?.personalDetails?.borrowerName ||
                              autoSaveData?.personalDetails?.contactNumber ||
                              autoSaveData?.personalDetails?.relationToBorrower ||
                              financialData?.personalDetails?.patientName || 
                              financialData?.personalDetails?.borrowerName ||
                              financialData?.personalDetails?.contactNumber ||
                              financialData?.personalDetails?.relationToBorrower;
      
      if (hasExistingData) {
        // Don't overwrite existing data
        console.log('Preserving existing financial data from draft');
        isInitializedRef.current = true;
        return;
      }
      
      // Determine if patient is the policy holder
      const isPatientPolicyHolder = application.policyHolder?.isSelf ?? application.isSelf ?? true;
      
      // Debug logging for PAN card retrieval
      console.log('=== PAN Card Debug Info ===');
      console.log('isPatientPolicyHolder:', isPatientPolicyHolder);
      console.log('application.patient?.kyc?.panNumber:', application.patient?.kyc?.panNumber);
      console.log('application.patientPan:', application.patientPan);
      console.log('application.patient?.kyc?.pan:', application.patient?.kyc?.pan);
      console.log('application.patient?.pan:', application.patient?.pan);
      console.log('application.policyHolder?.kyc?.panNumber:', application.policyHolder?.kyc?.panNumber);
      console.log('application.policyHolderPan:', application.policyHolderPan);
      console.log('application.policyHolder?.kyc?.pan:', application.policyHolder?.kyc?.pan);
      console.log('application.policyHolder?.pan:', application.policyHolder?.pan);
      console.log('========================');
      
      setFinancialData(prevData => ({
        ...prevData,
        personalDetails: {
          ...prevData.personalDetails,
          // Patient details
          patientName: application.patient?.name || application.patientName || "",
          patientGender: application.patient?.gender || application.patientGender || "Male",
          
          // Borrower details - use policy holder if different from patient, otherwise use patient
          isPatientBorrowerSame: isPatientPolicyHolder,
          borrowerName: isPatientPolicyHolder 
            ? (application.patient?.name || application.patientName || "")
            : (application.policyHolder?.name || application.policyHolderName || ""),
          borrowerGender: isPatientPolicyHolder
            ? (application.patient?.gender || application.patientGender || "Male")
            : (application.policyHolder?.gender || application.policyHolderGender || application.patient?.gender || application.patientGender || "Male"),
          // Populate DOB from policy holder details saved by NewPatientApplication
          // Note: For Self cases, policyHolder.dob is still present and should be used
          borrowerDateOfBirth: (application.policyHolder?.dob || prevData.personalDetails.borrowerDateOfBirth || ""),
          relationToBorrower: isPatientPolicyHolder 
            ? "Self" 
            : (application.policyHolder?.relationshipWithPatient || application.relationshipWithPatient || ""),
          
          // Contact details - prefer policy holder if different, otherwise patient
          contactNumber: isPatientPolicyHolder
            ? (application.patient?.phone || application.patientPhone || "")
            : (application.policyHolder?.phone || application.policyHolderPhone || application.patient?.phone || application.patientPhone || ""),
          emailId: isPatientPolicyHolder
            ? (application.patient?.email || application.patientEmail || "")
            : (application.policyHolder?.email || application.policyHolderEmail || application.patient?.email || application.patientEmail || ""),
          
          // KYC details - prefer policy holder if different, otherwise patient
          aadharNumber: isPatientPolicyHolder
            ? (application.patient?.kyc?.aadharNumber || application.patientAadhar || "")
            : (application.policyHolder?.kyc?.aadharNumber || application.policyHolderAadhar || application.patient?.kyc?.aadharNumber || application.patientAadhar || ""),
          panNumber: (() => {
            // Comprehensive PAN retrieval with all possible fallbacks
            if (isPatientPolicyHolder) {
              // When patient is policy holder, check all patient PAN locations
              return application.patient?.kyc?.panNumber || 
                     application.patientPan || 
                     application.patient?.kyc?.pan ||
                     application.patient?.pan ||
                     "";
            } else {
              // When policy holder is different, check policy holder first, then patient as fallback
              return application.policyHolder?.kyc?.panNumber || 
                     application.policyHolderPan || 
                     application.policyHolder?.kyc?.pan ||
                     application.policyHolder?.pan ||
                     application.patient?.kyc?.panNumber || 
                     application.patientPan || 
                     application.patient?.kyc?.pan ||
                     application.patient?.pan ||
                     "";
            }
          })(),
          // Alternate contact from policy holder details when available
          alternateContactNumber: (application.policyHolder?.alternatePhone || prevData.personalDetails.alternateContactNumber || ""),
          
          // Auto-populate employment data intelligently from nested employment object
          occupation: application.employment?.status || application.employmentStatus || "",
          companyName: (() => {
            // Check if employment object exists first
            if (application.employment) {
              // Priority: workingCompanyName > designation > natureOfWork
              if (application.employment.workingCompanyName) return application.employment.workingCompanyName;
              if (application.employment.status === "employed" && application.employment.designation) return application.employment.designation;
              if ((application.employment.status === "self-employed" || application.employment.status === "business") && application.employment.natureOfWork) return application.employment.natureOfWork;
            }
            // Fallback to legacy structure
            if (application.workingCompanyName) return application.workingCompanyName;
            if (application.employmentStatus === "employed" && application.designation) return application.designation;
            if ((application.employmentStatus === "self-employed" || application.employmentStatus === "business") && application.natureOfWork) return application.natureOfWork;
            return "";
          })()
        },
        addressDetails: {
          ...prevData.addressDetails,
          // Auto-populate address data - use patient address for permanent address
          permanentAddress: {
            ...prevData.addressDetails.permanentAddress,
            address: isPatientPolicyHolder
              ? (application.patient?.address || application.patientAddress || "")
              : (application.policyHolder?.address || application.policyHolderAddress || application.patient?.address || application.patientAddress || ""),
            // Try to extract city and state from leadGenerator if available
            city: application.leadGenerator?.city || "",
            state: application.leadGenerator?.state || ""
          },
          // Also populate current address initially with same data
          currentAddress: {
            ...prevData.addressDetails.currentAddress,
            address: isPatientPolicyHolder
              ? (application.patient?.address || application.patientAddress || "")
              : (application.policyHolder?.address || application.policyHolderAddress || application.patient?.address || application.patientAddress || ""),
            city: application.leadGenerator?.city || "",
            state: application.leadGenerator?.state || ""
          }
        },
        hospitalInfo: {
          ...prevData.hospitalInfo,
          name: application.hospitalName || "",
          treatmentDetails: application.medical?.diagnosis || application.medicalDiagnosis || ""
        },
        loanDetails: {
          ...prevData.loanDetails,
          loanAmount: application.medical?.estimatedCost || application.estimatedCost || "",
          // Auto-populate monthly income from nested employment object or legacy structure
          totalMonthlyIncome: application.employment?.monthlyIncome || application.monthlyIncome || ""
        }
      }));
      
      // Mark as initialized after setting data from application
      isInitializedRef.current = true;
    }
  }, [application, autoSaveData]);

  // Auto-save functionality - save every 30 seconds when form data changes
  useEffect(() => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    // Skip auto-save if form is empty or just initialized
    const hasData = Object.values(financialData).some(value => {
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(v => v && v.toString().trim() !== '');
      }
      return value && value.toString().trim() !== '';
    });

    if (hasData) {
      const timer = setTimeout(() => {
        if (onAutoSave) {
          onAutoSave(financialData);
        }
      }, 30000); // 30 seconds

      setAutoSaveTimer(timer);
    }

    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [financialData, onAutoSave]);

  // Manual save function for immediate save
  const handleManualSave = async () => {
    if (onAutoSave) {
      try {
        await onAutoSave(financialData, true); // Pass true to indicate manual save
        
        // Show success notification
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        toast.innerHTML = `
          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
          <span>Draft saved successfully!</span>
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 3000);
      } catch (error) {
        console.error('Error saving draft:', error);
        // Show error notification
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        toast.innerHTML = `
          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
          </svg>
          <span>Failed to save draft. Please try again.</span>
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 3000);
      }
    }
  };

  const handlePersonalDetailsChange = (field, value) => {
    if (isReadOnly) return;
    
    setFinancialData(prevData => ({
      ...prevData,
      personalDetails: {
        ...prevData.personalDetails,
        [field]: value
      }
    }));

    // Auto-populate borrower name if same as patient
    if (field === 'isPatientBorrowerSame' && value) {
      setFinancialData(prevData => ({
        ...prevData,
        personalDetails: {
          ...prevData.personalDetails,
          borrowerName: prevData.personalDetails.patientName,
          relationToBorrower: "Self"
        }
      }));
    }
  };

  const handleAddressChange = (addressType, field, value) => {
    if (isReadOnly) return;
    
    setFinancialData(prevData => ({
      ...prevData,
      addressDetails: {
        ...prevData.addressDetails,
        [addressType]: {
          ...prevData.addressDetails[addressType],
          [field]: value
        }
      }
    }));

    // Auto-populate current address if same as permanent
    if (addressType === 'permanentAddress' && financialData.addressDetails.isCurrentPermanentSame) {
      setFinancialData(prevData => ({
        ...prevData,
        addressDetails: {
          ...prevData.addressDetails,
          currentAddress: {
            ...prevData.addressDetails.currentAddress,
            [field]: value
          }
        }
      }));
    }
  };

  const handleReferenceChange = (field, value) => {
    if (isReadOnly) return;
    
    setFinancialData(prevData => ({
      ...prevData,
      referenceDetails: {
        ...prevData.referenceDetails,
        [field]: value
      }
    }));
  };

  const handleHospitalInfoChange = (field, value) => {
    if (isReadOnly) return;
    
    setFinancialData(prevData => ({
      ...prevData,
      hospitalInfo: {
        ...prevData.hospitalInfo,
        [field]: value
      }
    }));
  };

  const handleBankDetailsChange = (field, value) => {
    if (isReadOnly) return;
    
    setFinancialData(prevData => ({
      ...prevData,
      borrowerBankDetails: {
        ...prevData.borrowerBankDetails,
        [field]: value
      }
    }));
  };

  const handleLoanDetailsChange = (field, value) => {
    if (isReadOnly) return;
    
    setFinancialData(prevData => ({
      ...prevData,
      loanDetails: {
        ...prevData.loanDetails,
        [field]: value
      }
    }));
  };

  const handleDocumentUpload = (documentType, file) => {
    setFinancialData(prevData => ({
      ...prevData,
      documents: {
        ...prevData.documents,
        [documentType]: {
          file: file,
          url: URL.createObjectURL(file),
          uploaded: true
        }
      }
    }));
  };

  const removeDocument = (documentType) => {
    setFinancialData(prevData => ({
      ...prevData,
      documents: {
        ...prevData.documents,
        [documentType]: {
          file: null,
          url: "",
          uploaded: false
        }
      }
    }));
  };

  const handleSubmit = async (status) => {
    const isValid = validateForm(status);
    if (!isValid) {
      return;
    }
    setLoading(true);
    try {
      const submissionData = {
        ...financialData,
        status,
        reviewedBy: user.uid,
        reviewedAt: new Date(),
        reviewDuration: Math.floor((new Date() - reviewStartTime) / 1000)
      };

      console.log('Submitting financial review:', { status, submissionData });
      await onSubmit(submissionData);
      console.log('Financial review submitted successfully');
    } catch (error) {
      console.error('Error submitting financial review:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get input props based on read-only status
  const getInputProps = (additionalProps = {}) => ({
    disabled: isReadOnly,
    readOnly: isReadOnly,
    className: `w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`,
    ...additionalProps
  });

  // Helper function to wrap onChange handlers to prevent changes when read-only
  const getOnChangeHandler = (handler) => {
    return isReadOnly ? () => {} : handler;
  };

  // Clear a specific field error when user edits
  const clearFieldError = (field) => {
    setValidationErrors((prev) => {
      if (!prev || !prev[field]) return prev || {};
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Validate form: enforce payment preference and conditional lender fields on approval
  const validateForm = (status) => {
    const errors = {};
    if (status === 'approved') {
      if (!financialData.paymentOption) {
        errors.paymentOption = 'Please select a payment preference.';
      }
      if (financialData.paymentOption === 'loan') {
        if (!financialData.loanDetails?.lenderName?.trim()) {
          errors.lenderName = 'Lender Name is required when Loan is selected.';
        }
        if (!financialData.loanDetails?.loanId?.trim()) {
          errors.loanId = 'Loan ID is required when Loan is selected.';
        }
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  const renderPersonalDetails = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">
              Personal Details Verification
            </h4>
            <p className="text-sm text-blue-700 mt-1">
              Verify and complete all personal details before proceeding with financial assessment.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Patient & Borrower Information</h4>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isPatientBorrowerSame"
              checked={financialData.personalDetails.isPatientBorrowerSame}
              onChange={(e) => handlePersonalDetailsChange("isPatientBorrowerSame", e.target.checked)}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              disabled={isReadOnly}
            />
            <label htmlFor="isPatientBorrowerSame" className="text-sm font-medium text-gray-700">
              Is patient and borrower are the same?
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient Name *
            </label>
            <input
              type="text"
              value={financialData.personalDetails.patientName}
              onChange={(e) => !isReadOnly && handlePersonalDetailsChange("patientName", e.target.value)}
              {...getInputProps({ placeholder: "Enter patient name" })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name of the Borrower *
            </label>
            <input
              type="text"
              value={financialData.personalDetails.borrowerName}
              onChange={(e) => !isReadOnly && handlePersonalDetailsChange("borrowerName", e.target.value)}
              {...getInputProps({ 
                placeholder: "Enter borrower name",
                disabled: financialData.personalDetails.isPatientBorrowerSame || isReadOnly 
              })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relation of borrower to Patient
            </label>
            <select
              value={financialData.personalDetails.relationToBorrower}
              onChange={(e) => !isReadOnly && handlePersonalDetailsChange("relationToBorrower", e.target.value)}
              {...getInputProps()}
            >
              <option value="">Select Relationship</option>
              <option value="Self">Self</option>
              <option value="Spouse">Spouse</option>
              <option value="Father">Father</option>
              <option value="Mother">Mother</option>
              <option value="Son">Son</option>
              <option value="Daughter">Daughter</option>
              <option value="Brother">Brother</option>
              <option value="Sister">Sister</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Borrower Date of Birth
            </label>
            <input
              type="date"
              value={financialData.personalDetails.borrowerDateOfBirth}
              onChange={(e) => !isReadOnly && handlePersonalDetailsChange("borrowerDateOfBirth", e.target.value)}
              {...getInputProps()}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gender of Patient *
            </label>
            <select
              value={financialData.personalDetails.patientGender}
              onChange={(e) => handlePersonalDetailsChange("patientGender", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Contact & Professional Details</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Contact Number *
            </label>
            <input
              type="tel"
              value={financialData.personalDetails.contactNumber}
              onChange={(e) => !isReadOnly && handlePersonalDetailsChange("contactNumber", e.target.value)}
              {...getInputProps({ placeholder: "Enter contact number" })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email ID *
            </label>
            <input
              type="email"
              value={financialData.personalDetails.emailId}
              onChange={(e) => handlePersonalDetailsChange("emailId", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Occupation
            </label>
            <input
              type="text"
              value={financialData.personalDetails.occupation}
              onChange={(e) => handlePersonalDetailsChange("occupation", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter occupation"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={financialData.personalDetails.companyName}
              onChange={(e) => handlePersonalDetailsChange("companyName", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Entity name if business, company name if salaried"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Aadhar Number *
            </label>
            <input
              type="text"
              value={financialData.personalDetails.aadharNumber}
              onChange={(e) => handlePersonalDetailsChange("aadharNumber", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter Aadhar number"
              maxLength="12"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PAN Number *
            </label>
            <input
              type="text"
              value={financialData.personalDetails.panNumber}
              onChange={(e) => handlePersonalDetailsChange("panNumber", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter PAN number"
              maxLength="10"
              style={{ textTransform: 'uppercase' }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alternate Contact Number
            </label>
            <input
              type="tel"
              value={financialData.personalDetails.alternateContactNumber}
              onChange={(e) => handlePersonalDetailsChange("alternateContactNumber", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter alternate contact number"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderAddressDetails = () => (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Home className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-green-900">Address Information</h4>
            <p className="text-sm text-green-700 mt-1">Provide both permanent and current address details for verification.</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Permanent Address Details</h4>

        <div className="flex items-center space-x-2 mb-4">
          <input
            type="checkbox"
            id="isCurrentPermanentSame"
            checked={financialData.addressDetails.isCurrentPermanentSame}
            onChange={(e) => !isReadOnly && setFinancialData(prevData => ({
              ...prevData,
              addressDetails: {
                ...prevData.addressDetails,
                isCurrentPermanentSame: e.target.checked,
                ...(e.target.checked && { currentAddress: prevData.addressDetails.permanentAddress })
              }
            }))}
            className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            disabled={isReadOnly}
          />
          <label htmlFor="isCurrentPermanentSame" className="text-sm font-medium text-gray-700">Is Current and Permanent Address are the same?</label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permanent Pincode *</label>
            <input
              type="text"
              value={financialData.addressDetails.permanentAddress.pincode}
              onChange={(e) => handleAddressChange("permanentAddress", "pincode", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter pincode"
              maxLength="6"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permanent State *</label>
            <input
              type="text"
              value={financialData.addressDetails.permanentAddress.state}
              onChange={(e) => handleAddressChange("permanentAddress", "state", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter state"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permanent District *</label>
            <input
              type="text"
              value={financialData.addressDetails.permanentAddress.district}
              onChange={(e) => handleAddressChange("permanentAddress", "district", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter district"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Permanent City *</label>
            <input
              type="text"
              value={financialData.addressDetails.permanentAddress.city}
              onChange={(e) => handleAddressChange("permanentAddress", "city", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter city"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Permanent Address *</label>
          <textarea
            value={financialData.addressDetails.permanentAddress.address}
            onChange={(e) => handleAddressChange("permanentAddress", "address", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter complete permanent address"
            rows="3"
          />
        </div>

        <h4 className="text-lg font-semibold text-gray-900 pt-6">Current Address Details</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current Pincode *</label>
            <input
              type="text"
              value={financialData.addressDetails.currentAddress.pincode}
              onChange={(e) => handleAddressChange("currentAddress", "pincode", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter pincode"
              maxLength="6"
              disabled={financialData.addressDetails.isCurrentPermanentSame}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current State *</label>
            <input
              type="text"
              value={financialData.addressDetails.currentAddress.state}
              onChange={(e) => handleAddressChange("currentAddress", "state", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter state"
              disabled={financialData.addressDetails.isCurrentPermanentSame}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current District *</label>
            <input
              type="text"
              value={financialData.addressDetails.currentAddress.district}
              onChange={(e) => handleAddressChange("currentAddress", "district", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter district"
              disabled={financialData.addressDetails.isCurrentPermanentSame}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Current City *</label>
            <input
              type="text"
              value={financialData.addressDetails.currentAddress.city}
              onChange={(e) => handleAddressChange("currentAddress", "city", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Enter city"
              disabled={financialData.addressDetails.isCurrentPermanentSame}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Current Address *</label>
          <textarea
            value={financialData.addressDetails.currentAddress.address}
            onChange={(e) => handleAddressChange("currentAddress", "address", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter complete current address"
            rows="3"
            disabled={financialData.addressDetails.isCurrentPermanentSame}
          />
        </div>
      </div>
    </div>
  );

  const renderReferenceDetails = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <User className="h-5 w-5 text-purple-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-purple-900">
              Reference Details
            </h4>
            <p className="text-sm text-purple-700 mt-1">
              Provide reference person details for verification purposes.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reference Name *
          </label>
          <input
            type="text"
            value={financialData.referenceDetails.name}
            onChange={(e) => handleReferenceChange("name", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter reference person name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contact Number of Reference Person *
          </label>
          <input
            type="tel"
            value={financialData.referenceDetails.contactNumber}
            onChange={(e) => handleReferenceChange("contactNumber", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter contact number"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Relationship With the Reference Person
          </label>
          <input
            type="text"
            value={financialData.referenceDetails.relationship}
            onChange={(e) => handleReferenceChange("relationship", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter relationship"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email ID of the Reference Person
          </label>
          <input
            type="email"
            value={financialData.referenceDetails.emailId}
            onChange={(e) => handleReferenceChange("emailId", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter email address"
          />
        </div>
      </div>
    </div>
  );

  const renderHospitalInfo = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Building className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">
              Hospital Information
            </h4>
            <p className="text-sm text-blue-700 mt-1">
              Hospital and treatment details for the application.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name of the Hospital *
          </label>
          <input
            type="text"
            value={financialData.hospitalInfo.name}
            onChange={(e) => handleHospitalInfoChange("name", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter hospital name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Treatment Details *
          </label>
          <textarea
            value={financialData.hospitalInfo.treatmentDetails}
            onChange={(e) => handleHospitalInfoChange("treatmentDetails", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter detailed treatment information"
            rows="4"
          />
        </div>
      </div>
    </div>
  );

  const renderBankDetails = () => (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <CreditCard className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-green-900">
              Borrower Bank Details
            </h4>
            <p className="text-sm text-green-700 mt-1">
              Bank account details for loan disbursal.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bank Name *
          </label>
          <input
            type="text"
            value={financialData.borrowerBankDetails.bankName}
            onChange={(e) => handleBankDetailsChange("bankName", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter bank name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Branch *
          </label>
          <input
            type="text"
            value={financialData.borrowerBankDetails.branch}
            onChange={(e) => handleBankDetailsChange("branch", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter branch name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Number *
          </label>
          <input
            type="text"
            value={financialData.borrowerBankDetails.accountNumber}
            onChange={(e) => handleBankDetailsChange("accountNumber", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter account number"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            IFSC Code *
          </label>
          <input
            type="text"
            value={financialData.borrowerBankDetails.ifscCode}
            onChange={(e) => handleBankDetailsChange("ifscCode", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter IFSC code"
            maxLength="11"
            style={{ textTransform: 'uppercase' }}
          />
        </div>
      </div>
    </div>
  );

  // New: Payment option selector (Hospital PDC vs Loan)
  const renderPaymentPreference = () => (
    <div className="space-y-4">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <DollarSign className="h-5 w-5 text-sky-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-sky-900">Payment Preference</h4>
            <p className="text-sm text-sky-700 mt-1">
              Choose whether this case will be settled via Hospital PDC or a Loan. Selecting Loan will open the loan information section.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer ${financialData.paymentOption === 'hospital_pdc' ? 'border-teal-400 bg-teal-50' : 'border-gray-200 bg-white'}`}>
          <input
            type="radio"
            name="paymentOption"
            value="hospital_pdc"
            checked={financialData.paymentOption === 'hospital_pdc'}
            onChange={(e) => {
              if (isReadOnly) return;
              clearFieldError('paymentOption');
              clearFieldError('lenderName');
              clearFieldError('loanId');
              setFinancialData(prev => ({ ...prev, paymentOption: e.target.value }));
            }}
            disabled={isReadOnly}
            className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
          />
          <div>
            <p className="font-medium text-gray-900">Hospital PDC</p>
            <p className="text-sm text-gray-600">Save preference only; no loan details required.</p>
          </div>
        </label>

        <label className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer ${financialData.paymentOption === 'loan' ? 'border-teal-400 bg-teal-50' : 'border-gray-200 bg-white'}`}>
          <input
            type="radio"
            name="paymentOption"
            value="loan"
            checked={financialData.paymentOption === 'loan'}
            onChange={(e) => {
              if (isReadOnly) return;
              clearFieldError('paymentOption');
              setFinancialData(prev => ({ ...prev, paymentOption: e.target.value }));
            }}
            disabled={isReadOnly}
            className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
          />
          <div>
            <p className="font-medium text-gray-900">Loan</p>
            <p className="text-sm text-gray-600">Provide lender and loan parameters below.</p>
          </div>
        </label>
      </div>
      {validationErrors?.paymentOption && (
        <p className="text-sm text-red-600">{validationErrors.paymentOption}</p>
      )}
    </div>
  );

  const renderLoanDetails = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <DollarSign className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-yellow-900">
              Loan Details
            </h4>
            <p className="text-sm text-yellow-700 mt-1">
              Financial assessment and loan parameters.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Lender Name{financialData.paymentOption === 'loan' ? ' *' : ''}
          </label>
          <input
            type="text"
            value={financialData.loanDetails.lenderName}
            onChange={(e) => { clearFieldError('lenderName'); handleLoanDetailsChange("lenderName", e.target.value); }}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${validationErrors?.lenderName ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Enter lender name"
          />
          {validationErrors?.lenderName && (
            <p className="text-sm text-red-600 mt-1">{validationErrors.lenderName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan ID{financialData.paymentOption === 'loan' ? ' *' : ''}
          </label>
          <input
            type="text"
            value={financialData.loanDetails.loanId}
            onChange={(e) => { clearFieldError('loanId'); handleLoanDetailsChange("loanId", e.target.value); }}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${validationErrors?.loanId ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Enter loan ID / application number"
          />
          {validationErrors?.loanId && (
            <p className="text-sm text-red-600 mt-1">{validationErrors.loanId}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Total Income (Monthly) *
          </label>
          <input
            type="number"
            min="0"
            value={financialData.loanDetails.totalMonthlyIncome}
            onChange={(e) => handleLoanDetailsChange("totalMonthlyIncome", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter monthly income"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Loan Amount *
          </label>
          <input
            type="number"
            min="0"
            value={financialData.loanDetails.loanAmount}
            onChange={(e) => handleLoanDetailsChange("loanAmount", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter loan amount"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Scheme (No. of months)
          </label>
          <select
            value={financialData.loanDetails.schemeMonths}
            onChange={(e) => handleLoanDetailsChange("schemeMonths", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="6">6 Months</option>
            <option value="12">12 Months</option>
            <option value="18">18 Months</option>
            <option value="24">24 Months</option>
            <option value="36">36 Months</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Interest/Subvention %
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={financialData.loanDetails.interestSubventionPercent}
            onChange={(e) => handleLoanDetailsChange("interestSubventionPercent", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter interest percentage"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Processing Fee %
          </label>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={financialData.loanDetails.processingFeePercent}
            onChange={(e) => handleLoanDetailsChange("processingFeePercent", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="Enter processing fee percentage"
          />
        </div>
      </div>
    </div>
  );

  const renderDocumentUpload = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <FileText className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-red-900">
              Upload Documents
            </h4>
            <p className="text-sm text-red-700 mt-1">
              Please upload all required documents for verification. Documents marked with * are mandatory.
            </p>
          </div>
        </div>
      </div>

      {(() => {
        const isSelfPolicy = application?.policyHolder?.isSelf ?? true;
        const docs = application?.documents || {};
        const mapToHospitalDoc = (key) => {
          switch (key) {
            case 'aadharCardFront':
              return docs[isSelfPolicy ? 'patientAadharFrontDoc' : 'policyHolderAadharFrontDoc'] || null;
            case 'aadharCardBack':
              return docs[isSelfPolicy ? 'patientAadharBackDoc' : 'policyHolderAadharBackDoc'] || null;
            case 'borrowerPanCard':
              return docs[isSelfPolicy ? 'patientPanDoc' : 'policyHolderPanDoc'] || null;
            case 'bankStatement':
              return docs[isSelfPolicy ? 'patientChequeOrStatementDoc' : 'policyHolderChequeOrStatementDoc'] || null;
            default:
              return null;
          }
        };

        const uploadItems = [
          { key: 'aadharCardFront', label: 'Aadhar Card Front *', required: true },
          { key: 'aadharCardBack', label: 'Aadhar Card Back *', required: true },
          { key: 'borrowerPanCard', label: 'Borrower PAN Card *', required: true },
          { key: 'relationshipProof', label: 'Relationship Proof', required: false },
          { key: 'cancelledCheque', label: 'Cancelled Cheque', required: false },
          { key: 'incomeProof', label: 'Income Proof (ITR or Salary Slip)', required: false },
          { key: 'hospitalBill', label: 'Hospital Bill', required: false },
          { key: 'bankStatement', label: 'Bank Statement', required: false }
        ];

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {uploadItems.map((doc) => {
              const preUploaded = mapToHospitalDoc(doc.key);
              const showAsPreUploaded = ['aadharCardFront','aadharCardBack','borrowerPanCard','bankStatement'].includes(doc.key) && !!preUploaded;
              return (
                <div key={doc.key} className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">{doc.label}</label>

                  {showAsPreUploaded ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="min-w-0 mr-2">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="text-sm text-green-800 truncate">Already uploaded in hospital application ({isSelfPolicy ? 'Patient' : 'Policyholder'})</span>
                        </div>
                        <p className="text-xs text-green-700 mt-1 truncate">{preUploaded.fileName || 'Document'}</p>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <button type="button" onClick={() => openPreview(preUploaded)} className="inline-flex items-center px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">Preview</button>
                        <a href={preUploaded.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">Open</a>
                      </div>
                    </div>
                  ) : (
                    financialData.documents[doc.key].uploaded ? (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="text-sm text-green-800">Document uploaded</span>
                        </div>
                        {!isReadOnly && (
                          <button onClick={() => removeDocument(doc.key)} className="text-red-600 hover:text-red-800" type="button">
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className={`border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-teal-400 transition-colors upload-area ${isReadOnly ? 'opacity-50 pointer-events-none' : ''}`}>
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <div className="text-sm text-gray-600 mb-2">{isReadOnly ? 'Document upload disabled (approved)' : 'Click to upload or drag and drop'}</div>
                        <input type="file" onChange={(e) => { const file = e.target.files[0]; if (file && !isReadOnly) { handleDocumentUpload(doc.key, file); } }} className="hidden" id={`upload-${doc.key}`} accept=".pdf,.jpg,.jpeg,.png" disabled={isReadOnly} />
                        <label htmlFor={`upload-${doc.key}`} className={`cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}>Choose File</label>
                        <div className="text-xs text-gray-500 mt-1">PDF, PNG, JPG up to 10MB</div>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );

  // Group documents saved by NewPatientApplication into categories
  const groupSubmittedDocuments = () => {
    const docs = application?.documents || {};
    const entries = Object.entries(docs);
    const groups = {
      patientKYC: [],
      policyHolderKYC: [],
      policyCopiesPatient: [],
      policyCopiesHolder: [],
      medicalDocs: [],
      medicalHistory: [],
      investigationReports: [],
      chequeOrStatement: [],
      treatmentDocs: [],
      others: []
    };

    entries.forEach(([key, meta]) => {
      if (!meta || !meta.url) return;
      const item = { key, ...meta };
      if (key.startsWith('patientAadhar') || key === 'patientPanDoc' || key === 'patientAddressProofDoc') {
        groups.patientKYC.push(item);
      } else if (key.startsWith('policyHolderAadhar') || key === 'policyHolderPanDoc' || key === 'policyHolderAddressProofDoc') {
        groups.policyHolderKYC.push(item);
      } else if (key.startsWith('patientPolicyCopyDoc_')) {
        groups.policyCopiesPatient.push(item);
      } else if (key.startsWith('policyHolderPolicyCopyDoc_')) {
        groups.policyCopiesHolder.push(item);
      } else if (key.startsWith('medicalDoc_')) {
        groups.medicalDocs.push(item);
      } else if (key.startsWith('medicalHistoryDoc_')) {
        groups.medicalHistory.push(item);
      } else if (key.startsWith('investigationReport_')) {
        groups.investigationReports.push(item);
      } else if (key === 'patientChequeOrStatementDoc' || key === 'policyHolderChequeOrStatementDoc') {
        groups.chequeOrStatement.push(item);
      } else if (key.startsWith('treatmentDoc_')) {
        groups.treatmentDocs.push(item);
      } else {
        groups.others.push(item);
      }
    });

    return groups;
  };

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Byte';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const renderSubmittedDocuments = () => {
    const hasDocs = application?.documents && Object.keys(application.documents).length > 0;
    const groups = groupSubmittedDocuments();
    const sections = [
      { key: 'patientKYC', title: 'Patient KYC' },
      { key: 'policyHolderKYC', title: 'Policyholder KYC' },
      { key: 'policyCopiesPatient', title: 'Policy Copies (Patient)' },
      { key: 'policyCopiesHolder', title: 'Policy Copies (Policyholder)' },
      { key: 'medicalDocs', title: 'Medical Documents' },
      { key: 'medicalHistory', title: 'Medical History Documents' },
      { key: 'investigationReports', title: 'Investigation Reports' },
      { key: 'chequeOrStatement', title: 'Cancelled Cheque / Bank Statement' },
      { key: 'treatmentDocs', title: 'Treatment Documents' },
      { key: 'others', title: 'Other Documents' }
    ];

  return (
      <div className="space-y-6">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <FileText className="h-5 w-5 text-slate-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-slate-900">Submitted Documents</h4>
              <p className="text-sm text-slate-700 mt-1">These files were uploaded in the hospital application and saved in Firebase Storage.</p>
            </div>
          </div>
        </div>

        {!hasDocs ? (
          <div className="text-sm text-gray-600 p-4 border rounded-lg bg-white">No documents found for this application.</div>
        ) : (
          sections.map((section) => {
            const items = groups[section.key] || [];
            if (items.length === 0) return null;
            return (
              <div key={section.key} className="bg-white border border-gray-200 rounded-lg">
                <div className="px-4 py-3 border-b">
                  <h5 className="text-sm font-semibold text-gray-800">{section.title} <span className="text-gray-400 font-normal">({items.length})</span></h5>
                </div>
                <ul className="divide-y">
                  {items.map((doc) => {
                    const uploadedAt = doc.uploadedAt?.toDate ? doc.uploadedAt.toDate() : (doc.uploadedAt ? new Date(doc.uploadedAt) : null);
                    const lower = (doc.fileName || doc.url || '').toLowerCase();
                    const isPdf = lower.endsWith('.pdf') || doc.url?.includes('content-type=application%2Fpdf');
                    const isImage = /(png|jpe?g|gif|webp|bmp)$/i.test(lower);
                    return (
                      <li key={doc.key} className="px-4 py-3 flex items-center justify-between">
                        <div className="min-w-0 mr-4">
                          <div className="flex items-center space-x-2">
                            <FileText className={`h-4 w-4 ${isPdf ? 'text-red-500' : isImage ? 'text-emerald-500' : 'text-gray-400'}`} />
                            <p className="text-sm font-medium text-gray-800 truncate">{doc.fileName || doc.key}</p>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            <span>{formatBytes(doc.size)}</span>
                            {uploadedAt && <span className="ml-2">• Uploaded {uploadedAt.toLocaleString()}</span>}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button type="button" onClick={() => openPreview(doc)} className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            Preview
                          </button>
                          <a href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            Open
                          </a>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}

        {/* Discharge/Dispatch Documents if any */}
        {Array.isArray(application?.dischargeDocuments) && application.dischargeDocuments.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-4 py-3 border-b">
              <h5 className="text-sm font-semibold text-gray-800">Discharge/Dispatch Documents <span className="text-gray-400 font-normal">({application.dischargeDocuments.length})</span></h5>
            </div>
            <ul className="divide-y">
              {application.dischargeDocuments.map((doc) => {
                const uploadedAt = doc.uploadedAt?.toDate ? doc.uploadedAt.toDate() : (doc.uploadedAt ? new Date(doc.uploadedAt) : null);
                const lower = (doc.fileName || doc.url || '').toLowerCase();
                const isPdf = lower.endsWith('.pdf') || doc.url?.includes('content-type=application%2Fpdf');
                const isImage = /(png|jpe?g|gif|webp|bmp)$/i.test(lower);
                return (
                  <li key={doc.id || doc.fileName || doc.url} className="px-4 py-3 flex items-center justify-between">
                    <div className="min-w-0 mr-4">
                      <div className="flex items-center space-x-2">
                        <FileText className={`h-4 w-4 ${isPdf ? 'text-red-500' : isImage ? 'text-emerald-500' : 'text-gray-400'}`} />
                        <p className="text-sm font-medium text-gray-800 truncate">{doc.title || doc.fileName || 'Document'}</p>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <span>{formatBytes(doc.size)}</span>
                        {uploadedAt && <span className="ml-2">• Uploaded {uploadedAt.toLocaleString()}</span>}
                        {doc.dispatchStatus && <span className="ml-2">• Dispatch: {doc.dispatchStatus}</span>}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button type="button" onClick={() => openPreview(doc)} className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        Preview
                      </button>
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        Open
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderActionButtons = () => {
    // If already approved, show read-only message instead of action buttons
  if (isReadOnly) {
      return (
        <div className="bg-green-50 border-t border-green-200 px-6 py-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2 text-green-800">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium">
                Financial Review Approved - Form is now read-only
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleManualSave}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {loading ? 'Saving...' : 'Save Draft'}
            </button>
            {saveSuccess && (
              <div className="flex items-center text-green-600 text-sm">
                <CheckCircle className="h-4 w-4 mr-1" />
                Draft saved successfully
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {onRequestInfo && (
              <button
                onClick={() => onRequestInfo(financialData)}
                className="inline-flex items-center px-4 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Request Info
              </button>
            )}

            <button
              onClick={() => handleSubmit('rejected')}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Reject
            </button>

            <button
              onClick={() => handleSubmit('approved')}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Approve
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {preview.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-800 truncate pr-4">{preview.name || 'Document preview'}</h3>
              <button onClick={closePreview} className="text-gray-500 hover:text-gray-700" aria-label="Close preview">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 bg-gray-50">
              <div className="bg-white rounded border overflow-auto max-h-[70vh]">
                {preview.type === 'image' ? (
                  <img src={preview.url} alt={preview.name} className="max-w-full h-auto mx-auto" />
                ) : preview.type === 'pdf' ? (
                  <iframe title={preview.name} src={preview.url} className="w-full h-[70vh]" />
                ) : (
                  <div className="p-6 text-center">
                    <p className="text-sm text-gray-700 mb-4">Preview not available for this file type.</p>
                    <a href={preview.url} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center">Open in new tab</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
  {isReadOnly && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-green-900">
                Financial Review Completed
              </h4>
              <p className="text-sm text-green-700 mt-1">
                This application has been approved. The form is now in read-only mode for reference.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {renderPersonalDetails()}
      {renderAddressDetails()}
      {renderReferenceDetails()}
      {/* {renderHospitalInfo()} */}
      {renderBankDetails()}
      {renderPaymentPreference()}
      {financialData.paymentOption === 'loan' && renderLoanDetails()}
  {/* {renderSubmittedDocuments()} */}
      {renderDocumentUpload()}
      {renderActionButtons()}
    </div>
  );
};

export default FinancialReviewForm;
